/**
 * The header bell's pure model (PR 8): everything HeaderBell decides that a
 * test can check without a DOM — the badge, where an item goes, which keys are
 * unread, the preferences rows, and the two click paths.
 *
 * The bell is the ONE attention surface in the header now (it replaced the
 * "Notifications 75" link, the Alerts pill and the "Needs attention" health
 * pill). The server already decided what each person sees (backend/
 * notifications/feed.py: severity-gated sections, per-user read state,
 * preferences); this file only turns that answer into words and actions.
 */
import { OPEN_PENDING_TRAY_EVENT } from '../../lib/api'
import type {
  NotificationCategory, NotificationFeed, NotificationItem, NotificationSeverity,
} from '../../lib/notificationsApi'

/** The badge's text: nothing at 0, the count up to 99, then "99+". */
export function badgeText(n: number): string | null {
  if (!Number.isFinite(n) || n <= 0) return null
  return n > 99 ? '99+' : String(Math.floor(n))
}

export type BadgeTone = 'danger' | 'warning' | 'info'

/** The badge tint follows the worst UNREAD severity. */
export function badgeTone(worst: NotificationSeverity | null | undefined): BadgeTone {
  if (worst === 'critical' || worst === 'error') return 'danger'
  if (worst === 'warning') return 'warning'
  return 'info'
}

/** Semantic token classes for a badge tone (never a raw palette). */
export const BADGE_CLASS: Record<BadgeTone, string> = {
  danger: 'bg-danger text-danger-fg',
  warning: 'bg-warning text-ink-inverse',
  info: 'bg-info text-ink-inverse',
}

/** The little dot in front of an item, by severity. */
export const SEVERITY_DOT: Record<NotificationSeverity, string> = {
  critical: 'bg-danger',
  error: 'bg-danger',
  warning: 'bg-warning',
  info: 'bg-info',
}

/** What clicking an item does: open the Pending changes tray, go to a page,
 *  or nothing (a roll-up like "Some data may be delayed" has no page). */
export type ItemTarget =
  | { kind: 'tray' }
  | { kind: 'link'; href: string }
  | { kind: 'none' }

export function itemTarget(item: Pick<NotificationItem, 'action' | 'href'>): ItemTarget {
  if (item.action === 'open_pending_tray') return { kind: 'tray' }
  if (item.href) return { kind: 'link', href: item.href }
  return { kind: 'none' }
}

/** Every unread item key in the feed, in section order. */
export function unreadKeys(feed: NotificationFeed | null | undefined): string[] {
  if (!feed) return []
  return feed.sections.flatMap((s) => s.items.filter((i) => i.unread).map((i) => i.key))
}

/** "36 times" — shown only when something happened more than once. */
export function countLabel(count: number): string | null {
  return count > 1 ? `${count} times` : null
}

/** The time on a bell row. An item that keeps happening shows when it LAST
 *  happened, labelled ("last 1h ago"): its opening time next to "36 times"
 *  read as "stopped 12 days ago". Otherwise when it opened or changed. */
export function itemWhen(
  item: Pick<NotificationItem, 'count' | 'changed_at' | 'last_seen_at'>,
): { prefix: string; at: string } | null {
  if (item.count > 1 && item.last_seen_at) return { prefix: 'last ', at: item.last_seen_at }
  const at = item.changed_at ?? item.last_seen_at
  return at ? { prefix: '', at } : null
}

/** The bell button's accessible name. */
export function bellLabel(unread: number, backendDown: boolean): string {
  if (backendDown) return "Notifications — ClickPay can't reach its server"
  return `Notifications, ${unread} unread`
}

/** The top item while the server cannot be reached (3 missed health checks). */
export const BACKEND_DOWN_TEXT = "ClickPay can't reach its server. Numbers may be out of date."

/** Health checks in a row that must fail before the bell says the server is
 *  unreachable — the same rule the old health pill used (~3 minutes at 60 s). */
export const PROBE_FAIL_ESCALATE = 3

export type PrefRow = { category: NotificationCategory; label: string; hint: string }

const PREF_ROWS: readonly PrefRow[] = [
  { category: 'approvals', label: 'Approvals waiting', hint: 'Rule changes that need your OK' },
  { category: 'pending_changes', label: 'My pending changes', hint: 'Edits you staged that have not gone to Amazon' },
  { category: 'business_alerts', label: 'Business alerts', hint: 'Buy Box, budgets, stock and listings' },
  { category: 'data', label: 'Data delays', hint: 'When late data changes the numbers' },
  { category: 'system', label: 'System problems', hint: 'Background jobs, backups, the database' },
]

/** The preferences rows this viewer can change — "System problems" is admins only. */
export function prefRows(isAdmin: boolean): PrefRow[] {
  return PREF_ROWS.filter((r) => isAdmin || r.category !== 'system')
}

/** What an item click needs from the outside world (injected so it is testable). */
export type ActivateDeps = {
  markRead: (keys: string[]) => Promise<unknown>
  navigate: (href: string) => void
  dispatch: (event: Event) => void
  close: () => void
  /** Re-read the bell after the read is recorded. */
  refresh: () => void
}

/**
 * Click on an item: record it read (never blocks the navigation — a failed
 * write only means it stays unread), close the panel, then open the tray or
 * go to the fix.
 */
export function activateItem(item: NotificationItem, deps: ActivateDeps): void {
  if (item.unread) {
    void deps.markRead([item.key]).then(() => deps.refresh(), () => { /* stays unread */ })
  }
  deps.close()
  const target = itemTarget(item)
  if (target.kind === 'tray') deps.dispatch(new CustomEvent(OPEN_PENDING_TRAY_EVENT))
  else if (target.kind === 'link') deps.navigate(target.href)
}

/** "Mark all read": one request for the whole bell, then one re-read. */
export async function markAllThenRefetch(
  markAllRead: () => Promise<unknown>,
  refetch: () => Promise<unknown> | void,
): Promise<void> {
  await markAllRead()
  await refetch()
}
